import React from 'react';
import ReactSelect from '@components/atoms/ReactSelect';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Task } from '@models/task.model';
import { Event } from '@models/event.model';
import { SelectOption } from '@components/atoms/ReactSelect';
import { PrecedentConflict } from '@utils/precedentConflicts';
import { formatDateOnly } from '@utils/formatDate';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  components as reactSelectComponents,
  MultiValueRemoveProps,
} from 'react-select';
import { useTranslation } from '@hooks/useTranslation';
import { MAX_PARENT_DEPTH } from '@utils/taskHierarchy';

interface DependenciesTabProps {
  tasksWithDependencies: Task[];
  allTasks: Task[];
  cycleCreatingTaskIds?: Set<string>;
  events: Event[];
  preseding: string[];
  presedingEvent: string[];
  setPreseding: (taskIds: string[]) => void;
  setPresedingEvent: (eventIds: string[]) => void;
  setFieldValue?: (field: string, value: any) => void; // Add Formik setFieldValue
  /** Precedents that end after this task starts. Empty when there is nothing wrong. */
  conflicts?: PrecedentConflict[];
  /** This task's start date, so the notice can name both sides of the clash. */
  taskStartTime?: string | Date | null;
}

const PATH_SEPARATOR = ' → ';

type DisabledReason = 'cycle' | 'redundant';

interface TaskOption extends SelectOption {
  depth: number;
  hasChildren: boolean;
  path: string;
  isMain: boolean;
  disabledReason?: DisabledReason;
}

// Collapse consecutive identical segments — keeps the path informative when
// it conveys hierarchy (Foundation → Concrete pour) but strips meaningless
// duplication when a child is named like its parent (Stairs → Stairs).
const dedupePath = (path: string): string => {
  const segments = path.split(PATH_SEPARATOR);
  const out: string[] = [];
  for (const seg of segments) {
    if (out.length === 0 || out[out.length - 1] !== seg) {
      out.push(seg);
    }
  }
  return out.join(PATH_SEPARATOR);
};

interface TaskOptionGroup {
  label: string;
  options: TaskOption[];
}

const buildTaskPathInfo = (
  taskId: string,
  taskMap: Map<string, Task>,
): { path: string; depth: number } => {
  const chain: string[] = [];
  const visited = new Set<string>();
  let currentId: string | null | undefined = taskId;
  let steps = 0;

  while (currentId && steps <= MAX_PARENT_DEPTH) {
    if (visited.has(currentId)) break;
    visited.add(currentId);
    const task = taskMap.get(currentId);
    if (!task) break;
    chain.unshift(task.name);
    currentId = task.parentTaskId;
    steps++;
  }

  return {
    path: chain.join(PATH_SEPARATOR),
    depth: Math.max(0, chain.length - 1),
  };
};

const getStartMs = (task: Task): number => {
  if (!task.startTime) return Number.POSITIVE_INFINITY;
  const ms = new Date(task.startTime as any).getTime();
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
};

const compareByStartTime = (a: Task, b: Task): number => {
  const diff = getStartMs(a) - getStartMs(b);
  if (diff !== 0) return diff;
  return a.name.localeCompare(b.name);
};

const buildHierarchicalGroups = (
  tasks: Task[],
  taskMap: Map<string, Task>,
  allTaskMap: Map<string, Task>,
  otherGroupLabel: string,
): TaskOptionGroup[] => {
  // Bucket each task by its effective parent key. Three cases:
  // - Parent is in the selectable set → real child, nests under that parent.
  // - Parent exists upstream but was excluded (ancestor/descendant/cycle of
  //   the current task) → keep it bucketed under the excluded parent so we
  //   can still render the parent's name as a decorative group header and
  //   preserve the hierarchy context.
  // - No resolvable parent → true root.
  const childrenByParent = new Map<string | null, Task[]>();
  for (const task of tasks) {
    if (!task.taskId) continue;
    const pid = task.parentTaskId;
    let parentKey: string | null;
    if (pid && taskMap.has(pid)) {
      parentKey = pid;
    } else if (pid && allTaskMap.has(pid)) {
      parentKey = pid;
    } else {
      parentKey = null;
    }
    const list = childrenByParent.get(parentKey) ?? [];
    list.push(task);
    childrenByParent.set(parentKey, list);
  }

  childrenByParent.forEach((list) => {
    list.sort(compareByStartTime);
  });

  const visited = new Set<string>();
  const groups: TaskOptionGroup[] = [];
  const otherOptions: TaskOption[] = [];

  const walkDescendants = (
    parentKey: string,
    depth: number,
    parentPath: string,
    out: TaskOption[],
  ) => {
    const children = childrenByParent.get(parentKey) ?? [];
    for (const task of children) {
      if (!task.taskId || visited.has(task.taskId)) continue;
      visited.add(task.taskId);

      const path = `${parentPath}${PATH_SEPARATOR}${task.name}`;
      const hasChildren =
        (childrenByParent.get(task.taskId)?.length ?? 0) > 0;
      out.push({
        value: task.taskId,
        label: path,
        depth,
        hasChildren,
        path,
        isMain: false,
      });

      if (depth < MAX_PARENT_DEPTH) {
        walkDescendants(task.taskId, depth + 1, path, out);
      }
    }
  };

  // 1. Selectable roots (no parent in the data at all).
  const trueRoots = childrenByParent.get(null) ?? [];

  for (const root of trueRoots) {
    if (!root.taskId || visited.has(root.taskId)) continue;
    const childCount = childrenByParent.get(root.taskId)?.length ?? 0;

    if (childCount === 0) {
      visited.add(root.taskId);
      otherOptions.push({
        value: root.taskId,
        label: root.name,
        depth: 0,
        hasChildren: false,
        path: root.name,
        isMain: true,
      });
      continue;
    }

    visited.add(root.taskId);
    const groupOptions: TaskOption[] = [
      {
        value: root.taskId,
        label: root.name,
        depth: 0,
        hasChildren: true,
        path: root.name,
        isMain: true,
      },
    ];
    walkDescendants(root.taskId, 1, root.name, groupOptions);

    groups.push({
      label: root.name,
      options: groupOptions,
    });
  }

  // 2. Excluded-parent groups: the parent isn't selectable, but we still
  //    surface its name as a decorative group header so the user keeps the
  //    "this is a subtask of X" context.
  const excludedParentEntries = Array.from(childrenByParent.entries())
    .filter(([key]) => key !== null && !taskMap.has(key))
    .map(([key]) => allTaskMap.get(key as string))
    .filter((t): t is Task => !!t)
    .sort(compareByStartTime);

  for (const excludedParent of excludedParentEntries) {
    if (!excludedParent.taskId) continue;
    const groupOptions: TaskOption[] = [];
    walkDescendants(excludedParent.taskId, 1, excludedParent.name, groupOptions);
    if (groupOptions.length === 0) continue;

    groups.push({
      label: excludedParent.name,
      options: groupOptions,
    });
  }

  // 3. Anything we still haven't visited (parent missing entirely from the
  //    data) goes to "Other tasks" with its best-effort path.
  for (const task of tasks) {
    if (!task.taskId || visited.has(task.taskId)) continue;
    const info = buildTaskPathInfo(task.taskId, allTaskMap);
    otherOptions.push({
      value: task.taskId,
      label: info.path || task.name,
      depth: 0,
      hasChildren: false,
      path: info.path || task.name,
      isMain: true,
    });
    visited.add(task.taskId);
  }

  if (otherOptions.length > 0) {
    otherOptions.sort((a, b) => a.label.localeCompare(b.label));
    groups.push({
      label: otherGroupLabel,
      options: otherOptions,
    });
  }

  return groups;
};

// Walks up the precedent graph from each currently-selected predecessor and
// collects every task that is *transitively* implied. A direct selection of
// any of those would be redundant (e.g. C depends on B, B depends on A → C
// already implies A; selecting A on C is redundant).
const computeRedundantPrecedentIds = (
  selectedPrecedentIds: string[],
  taskMap: Map<string, Task>,
): Set<string> => {
  const redundant = new Set<string>();

  const visitAncestors = (taskId: string, guard: Set<string>) => {
    const task = taskMap.get(taskId);
    if (!task) return;
    const parents = task.presedentTaskId || [];
    for (const p of parents) {
      if (guard.has(p)) continue;
      guard.add(p);
      redundant.add(p);
      visitAncestors(p, guard);
    }
  };

  for (const id of selectedPrecedentIds) {
    visitAncestors(id, new Set([id]));
  }

  return redundant;
};

const formatTaskOptionLabel = (
  option: SelectOption,
  meta: { context: 'menu' | 'value' },
  tDependencies: { wouldCreateCycle: string; alreadyImplied: string; mainBadge: string },
) => {
  const taskOption = option as TaskOption;
  const path = taskOption.path ?? taskOption.label;
  const isDisabled = !!taskOption.isDisabled;

  // Selected chip: dedupes consecutive duplicated segments (e.g. when a
  // subtask shares the parent's name). Keeps Parent → Child context for
  // the common case.
  if (meta.context === 'value') {
    return <span>{dedupePath(path)}</span>;
  }

  const disabledHintText =
    taskOption.disabledReason === 'cycle'
      ? tDependencies.wouldCreateCycle
      : taskOption.disabledReason === 'redundant'
        ? tDependencies.alreadyImplied
        : null;
  const disabledHint =
    isDisabled && disabledHintText ? (
      <span className="ml-2 text-xs italic text-gray-400">
        {disabledHintText}
      </span>
    ) : null;

  const depth = taskOption.depth ?? 0;
  const segments = path.split(PATH_SEPARATOR);
  const displayName = segments[segments.length - 1] ?? path;

  // Root main with subtasks → name + "Main" badge (selectable as precedent).
  if (depth === 0 && taskOption.hasChildren) {
    return (
      <span
        className={`flex items-center ${
          isDisabled ? 'text-gray-400 italic' : ''
        }`}
      >
        <span className="font-semibold text-gray-900">{displayName}</span>
        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-700 rounded">
          {tDependencies.mainBadge}
        </span>
        {disabledHint}
      </span>
    );
  }

  // Standalone task (no children, no parent in scope).
  if (depth === 0) {
    return (
      <span className={isDisabled ? 'text-gray-400 italic' : 'text-gray-900'}>
        {displayName}
        {disabledHint}
      </span>
    );
  }

  // Descendant: indentation + ↳. Sub-header style if it has its own subtasks.
  const hasOwnChildren = taskOption.hasChildren;
  const textTone = isDisabled
    ? 'text-gray-400 italic'
    : hasOwnChildren
      ? 'text-gray-800 font-medium'
      : 'text-gray-700';
  return (
    <span
      className={`flex items-center ${textTone}`}
      style={{ paddingLeft: `${depth * 20}px` }}
    >
      <span className="text-gray-400 mr-2">↳</span>
      <span>{displayName}</span>
      {disabledHint}
    </span>
  );
};

// Sprint 24, story-115 test-plan Bug #1 (was sprint-23 story-112 / HINcpVzt).
// react-select's built-in multi-value Remove button computes its OWN
// aria-label independently of formatOptionLabel/getOptionLabel:
// `"Remove ".concat(children || 'option')`, where `children` is whatever the
// 'value'-context formatOptionLabel returned for that chip — a React
// element here, so it stringifies to "Remove [object Object]". The
// getOptionLabel added in sprint-23 never feeds that computation at all (it
// only affects search matching and other react-select internals), which is
// why that fix was ineffective. The only reliable fix is to replace the
// Remove sub-component itself and set its accessible name directly, from
// the same dedupePath(path) text the chip already displays. The visible "x"
// icon is unchanged — only the aria-label is corrected.
const makeMultiValueRemove = (removePrefix: string) => {
  const MultiValueRemove = (
    props: MultiValueRemoveProps<SelectOption, true>,
  ) => {
    const taskOption = props.data as TaskOption;
    const accessibleLabel = dedupePath(taskOption.path ?? taskOption.label);
    return (
      <reactSelectComponents.MultiValueRemove
        {...props}
        innerProps={{
          ...props.innerProps,
          'aria-label': `${removePrefix} ${accessibleLabel}`,
        }}
      />
    );
  };
  return MultiValueRemove;
};

export const DependenciesTab: React.FC<DependenciesTabProps> = ({
  tasksWithDependencies,
  allTasks,
  cycleCreatingTaskIds,
  events,
  preseding,
  presedingEvent,
  setPreseding,
  setPresedingEvent,
  setFieldValue,
  conflicts = [],
  taskStartTime,
}) => {
  const tTaskDetail = useTranslation('taskDetail');
  const tDependencies = tTaskDetail.dependencies;

  const taskMap = React.useMemo(
    () =>
      new Map(
        tasksWithDependencies
          .filter((t) => !!t.taskId)
          .map((t) => [t.taskId!, t]),
      ),
    [tasksWithDependencies],
  );

  const allTaskMap = React.useMemo(
    () =>
      new Map(
        allTasks.filter((t) => !!t.taskId).map((t) => [t.taskId!, t]),
      ),
    [allTasks],
  );

  const redundantPrecedentIds = React.useMemo(
    () => computeRedundantPrecedentIds(preseding, taskMap),
    [preseding, taskMap],
  );

  const taskGroups: TaskOptionGroup[] = React.useMemo(() => {
    const raw = buildHierarchicalGroups(
      tasksWithDependencies,
      taskMap,
      allTaskMap,
      tDependencies.otherTasksGroup,
    );
    return raw.map((group) => ({
      ...group,
      options: group.options.map((opt) => {
        const id = opt.value as string;
        const isAlreadySelected = preseding.includes(id);
        if (cycleCreatingTaskIds?.has(id) && !isAlreadySelected) {
          return { ...opt, isDisabled: true, disabledReason: 'cycle' };
        }
        if (redundantPrecedentIds.has(id) && !isAlreadySelected) {
          return { ...opt, isDisabled: true, disabledReason: 'redundant' };
        }
        return opt;
      }),
    }));
  }, [
    tasksWithDependencies,
    taskMap,
    allTaskMap,
    redundantPrecedentIds,
    cycleCreatingTaskIds,
    preseding,
    tDependencies.otherTasksGroup,
  ]);

  const flatTaskOptions: TaskOption[] = React.useMemo(
    () => taskGroups.flatMap((group) => group.options),
    [taskGroups],
  );

  // Convert events to ReactSelect options
  const eventOptions: SelectOption[] = events.map((event) => ({
    value: event.eventId!,
    label: event.name,
  }));

  // Get selected task options
  const selectedTaskOptions = flatTaskOptions.filter((option) =>
    preseding.includes(option.value as string),
  );

  // Get selected event options
  const selectedEventOptions = eventOptions.filter((option) =>
    presedingEvent.includes(option.value as string),
  );

  const handleTaskChange = (selectedOptions: any) => {
    const taskIds = Array.isArray(selectedOptions)
      ? selectedOptions.map((option) => option.value)
      : [];

    // Update both local state and Formik form values
    setPreseding(taskIds);
    if (setFieldValue) {
      setFieldValue('presedentTaskId', taskIds);
    }
  };

  const handleEventChange = (selectedOptions: any) => {
    const eventIds = Array.isArray(selectedOptions)
      ? selectedOptions.map((option) => option.value)
      : [];

    // Update both local state and Formik form values
    setPresedingEvent(eventIds);
    if (setFieldValue) {
      setFieldValue('presedentEventId', eventIds);
    }
  };

  return (
    <div className="space-y-6">
      {/* A precedent that finishes after this task starts is a scheduling
          contradiction. Warned, never blocked — the same call the save-time
          modal offers. */}
      {conflicts.length > 0 && (
        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
          <WarningAmberIcon
            className="mt-0.5 shrink-0 text-amber-500"
            sx={{ fontSize: '20px' }}
          />
          <div className="min-w-0">
            <p className="m-0 text-sm font-semibold text-amber-900 dark:text-amber-100">
              {tDependencies.conflictTitle}
            </p>
            <p className="mt-1 mb-0 text-xs text-amber-800 dark:text-amber-200">
              {conflicts.length === 1
                ? tDependencies.conflictThisEnds
                : tDependencies.conflictTheseEnd}{' '}
              {taskStartTime
                ? tDependencies.conflictAfterThisTaskStartsOn.replace(
                    '{date}',
                    formatDateOnly(taskStartTime),
                  )
                : tDependencies.conflictAfterThisTaskStarts}
              . {tDependencies.conflictSaveAnywayNote}
            </p>
            <ul className="mt-2 mb-0 space-y-1 pl-0 list-none">
              {conflicts.map((conflict) => (
                <li
                  key={`${conflict.type}-${conflict.id}`}
                  className="text-xs text-amber-900 dark:text-amber-100">
                  <span className="font-medium">{conflict.name}</span>
                  <span className="text-amber-700 dark:text-amber-300">
                    {' '}
                    — {conflict.type === 'event' ? tDependencies.conflictOccurs : tDependencies.conflictEnds} on{' '}
                    {formatDateOnly(conflict.endDate)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Preceding Tasks */}
      <FormGroup label={tDependencies.precedingTaskLabel} htmlFor="preceding-tasks">
        <ReactSelect
          // Flatten the groups: visual hierarchy is conveyed by per-option
          // depth/indent + the "Main" badge, so the group headings are
          // redundant noise. Order from buildHierarchicalGroups is preserved.
          options={flatTaskOptions}
          isMulti={true}
          value={selectedTaskOptions || []}
          onChange={handleTaskChange}
          placeholder={tDependencies.searchPrecedingTasksPlaceholder}
          isClearable={true}
          isSearchable={true}
          formatOptionLabel={(option, meta) => formatTaskOptionLabel(option, meta, tDependencies)}
          // Feeds react-select's own label-based option matching/filtering and
          // other internal a11y announcements (option focus, "N results"). Does
          // NOT feed the multi-value chip's Remove button — that one is fixed
          // separately below via the custom MultiValueRemove component, since
          // react-select builds that particular aria-label from formatOptionLabel's
          // 'value'-context output, not from this.
          getOptionLabel={(option) => dedupePath((option as TaskOption).path ?? option.label)}
          components={{ MultiValueRemove: makeMultiValueRemove(tDependencies.removeAriaLabelPrefix) }}
        />
      </FormGroup>

      {/* Preceding Events */}
      <FormGroup label={tDependencies.precedingEventLabel} htmlFor="preceding-events">
        <ReactSelect
          options={eventOptions}
          isMulti={true}
          value={selectedEventOptions || []}
          onChange={handleEventChange}
          placeholder={tDependencies.selectPrecedingEventsPlaceholder}
          isClearable={true}
          isSearchable={true}
        />
      </FormGroup>
    </div>
  );
};
